/* =============================================
   CANSEVEN YORESEL - THEME JS
   Shopify 2.0 Theme
   ============================================= */

/* --- Cart API --- */
class CartAPI {
  static async getCart() {
    const response = await fetch(window.routes.cart + '.js');
    return response.json();
  }

  static async addItem(formData) {
    const response = await fetch(window.routes.cartAdd + '.js', {
      method: 'POST',
      body: formData
    });
    return response.json();
  }

  static async updateItem(updates) {
    const response = await fetch(window.routes.cartUpdate + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return response.json();
  }

  static async changeItem(key, quantity) {
    const response = await fetch(window.routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    return response.json();
  }
}

/* --- Quantity Selector --- */
class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.quantity-selector__input') || this.querySelector('.ziba-product__qty-input') || this.querySelector('.mobile-pdp__qty-input');
    this.buttonMinus = this.querySelector('[data-action="minus"]');
    this.buttonPlus = this.querySelector('[data-action="plus"]');

    if (this.buttonMinus) {
      this.buttonMinus.addEventListener('click', this.handleChange.bind(this, -1));
    }
    if (this.buttonPlus) {
      this.buttonPlus.addEventListener('click', this.handleChange.bind(this, 1));
    }
    if (this.input) {
      this.input.addEventListener('change', this.handleInput.bind(this));
    }
  }

  handleChange(delta) {
    const currentValue = parseInt(this.input.value, 10) || 1;
    const newValue = Math.max(1, currentValue + delta);
    this.input.value = newValue;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleInput() {
    const value = parseInt(this.input.value, 10);
    if (isNaN(value) || value < 1) {
      this.input.value = 1;
    }
  }
}
if (!customElements.get('quantity-selector')) customElements.define('quantity-selector', QuantitySelector);

/* --- Variant Selector --- */
class VariantSelector extends HTMLElement {
  constructor() {
    super();
    this.form = this.closest('form');
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  onVariantChange() {
    this.updateOptions();
    this.updateVariant();

    if (!this.currentVariant) {
      this.setUnavailable();
      return;
    }

    this.updateURL();
    this.updateFormID();
    this.updatePrice();
    this.updateMedia();
    this.updatePickupAvailability();
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select, fieldset')).map((el) => {
      if (el.tagName === 'SELECT') {
        return el.value;
      }
      const checked = el.querySelector('input:checked');
      return checked ? checked.value : '';
    });
  }

  updateVariant() {
    const variants = JSON.parse(this.getAttribute('data-variants') || '[]');
    this.currentVariant = variants.find((variant) => {
      return variant.options.every((option, index) => option === this.options[index]);
    });
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    const url = new URL(window.location);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url);
  }

  updateFormID() {
    if (!this.currentVariant) return;
    const input = this.form?.querySelector('input[name="id"]');
    if (input) {
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updatePrice() {
    if (!this.currentVariant) return;
    const event = new CustomEvent('variant:change', {
      detail: { variant: this.currentVariant },
      bubbles: true
    });
    this.dispatchEvent(event);
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;
    const event = new CustomEvent('variant:media-change', {
      detail: { mediaId: this.currentVariant.featured_media.id },
      bubbles: true
    });
    this.dispatchEvent(event);
  }

  updatePickupAvailability() {
    const pickupEl = document.querySelector('pickup-availability');
    if (pickupEl && this.currentVariant) {
      pickupEl.fetchAvailability(this.currentVariant.id);
    }
  }

  setUnavailable() {
    const addButton = this.form?.querySelector('[type="submit"]');
    if (addButton) {
      addButton.setAttribute('disabled', '');
      addButton.textContent = window.variantStrings?.unavailable || 'Unavailable';
    }

    const priceEl = this.form?.querySelector('.price');
    if (priceEl) {
      priceEl.classList.add('hidden');
    }
  }
}
if (!customElements.get('variant-selector')) {
  customElements.define('variant-selector', VariantSelector);
}

/* --- Product Form --- */
class ProductForm extends HTMLElement {
  constructor() {
    super();
    this._bound = false;
  }

  connectedCallback() {
    if (this._bound) return;
    this.form = this.querySelector('form');
    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this._bound = true;
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    const submitButton = this.querySelector('[type="submit"]');
    if (submitButton.hasAttribute('disabled')) return;

    submitButton.setAttribute('disabled', '');
    submitButton.classList.add('loading');

    const formData = new FormData(this.form);

    try {
      const response = await CartAPI.addItem(formData);

      if (response.status) {
        this.handleError(response);
        return;
      }

      this.handleSuccess(response);
    } catch (error) {
      console.error('Add to cart error:', error);
    } finally {
      submitButton.removeAttribute('disabled');
      submitButton.classList.remove('loading');
    }
  }

  handleSuccess(item) {
    document.dispatchEvent(new CustomEvent('cart:item-added', {
      detail: { item },
      bubbles: true
    }));

    const cartCount = document.querySelectorAll('.cart-count');
    cartCount.forEach(async (el) => {
      const cart = await CartAPI.getCart();
      el.textContent = cart.item_count;
      el.classList.toggle('hidden', cart.item_count === 0);
    });
  }

  handleError(response) {
    const errorMessage = response.description || window.cartStrings?.error || 'Error';
    console.error('Cart error:', errorMessage);

    const errorEl = this.querySelector('.product-form__error');
    if (errorEl) {
      errorEl.textContent = errorMessage;
      errorEl.classList.remove('hidden');
    }
  }
}
if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

/* --- Drawer --- */
class DrawerComponent extends HTMLElement {
  constructor() {
    super();
    this.overlay = document.querySelector('.overlay');
    this.closeButtons = this.querySelectorAll('[data-drawer-close]');

    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', this.close.bind(this));
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', this.close.bind(this));
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  open() {
    this.classList.add('is-open');
    this.overlay?.classList.add('is-active');
    document.body.classList.add('overflow-hidden');
    this.trapFocus();
  }

  close() {
    this.classList.remove('is-open');
    this.overlay?.classList.remove('is-active');
    document.body.classList.remove('overflow-hidden');
    this.releaseFocus();
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  trapFocus() {
    const focusable = this.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    first.focus();

    this._trapHandler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    this.addEventListener('keydown', this._trapHandler);
  }

  releaseFocus() {
    if (this._trapHandler) {
      this.removeEventListener('keydown', this._trapHandler);
      this._trapHandler = null;
    }
  }
}
if (!customElements.get('drawer-component')) customElements.define('drawer-component', DrawerComponent);

/* --- Menu Drawer (mobile) --- */
class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
    this.openButton = document.querySelector('[data-menu-drawer-open]');
    if (this.openButton) {
      this.openButton.addEventListener('click', this.open.bind(this));
    }
  }

  open() {
    super.open();
    this.setAttribute('aria-hidden', 'false');
  }

  close() {
    super.close();
    this.setAttribute('aria-hidden', 'true');
    this.openButton?.focus();
  }
}
if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);

/* --- Cart Drawer --- */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.debouncedUpdate = this.debounce(this.changeItem.bind(this), 300);
    this.init();
  }

  init() {
    // Delegate click + change inside drawer
    this.addEventListener('click', this.handleEvent.bind(this));
    this.addEventListener('change', this.handleEvent.bind(this));

    // Overlay click closes drawer
    const overlay = this.querySelector('.drawer__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.close());
    }

    // Escape key closes drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });

    // Global cart triggers (header cart icon, mobile bottom nav, etc.)
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.header__cart, .mobile-bottom-nav__cart, .mobile-pdp__cart, .ziba-topbar__cart, [data-cart-drawer-trigger]');
      if (trigger) {
        e.preventDefault();
        this.refresh();
        this.open();
      }
      // Also intercept /cart links to open drawer instead (but not when drawer is already open)
      const cartLink = e.target.closest('a[href="/cart"]');
      if (cartLink && !this.isOpen()) {
        e.preventDefault();
        this.refresh();
        this.open();
      }

      // Bundle Add
      const bundleBtn = e.target.closest('.bundle-add-btn');
      if (bundleBtn) {
        this.addBundle(bundleBtn.dataset.bundleVariants, bundleBtn);
      }

      // Upsell Add
      const upsellBtn = e.target.closest('.upsell-add-btn');
      if (upsellBtn) {
        this.addUpsellItem(upsellBtn.dataset.variantId, upsellBtn);
      }
    });

    // Listen for cart:item-added from product forms / quick-add
    document.addEventListener('cart:item-added', (e) => {
      this.refresh();
      this.open();
      const itemName = e.detail?.item?.title || e.detail?.item?.product_title;
      this.showToast(itemName ? `${itemName} sepete eklendi!` : 'Ürün sepete eklendi!');
    });

    // Also listen on window for compatibility
    window.addEventListener('cart:item-added', (e) => {
      this.refresh();
      this.open();
    });
  }

  /* --- Open / Close --- */
  open() {
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  renderContents() {
    this.refresh();
    this.open();
  }

  /* --- Event Delegation --- */
  handleEvent(event) {
    const target = event.target;

    // Close button
    if (target.closest('.cart-drawer__close') || target.closest('[data-drawer-close]')) {
      this.close();
      return;
    }

    // Remove Item — set quantity to 0
    if (target.closest('.cart-drawer-item__remove')) {
      event.preventDefault();
      const item = target.closest('.cart-drawer-item');
      if (item) {
        const key = item.dataset.lineItemKey;
        if (key) {
          this.changeItem(key, 0);
        }
      }
      return;
    }

    // Quantity Plus
    if (target.closest('[data-action="plus"]')) {
      const item = target.closest('.cart-drawer-item');
      const input = item?.querySelector('.quantity-selector__input');
      if (item && input) {
        const key = item.dataset.lineItemKey;
        const newVal = Math.max(1, (parseInt(input.value) || 0) + 1);
        input.value = newVal;
        this.debouncedUpdate(key, newVal);
      }
      return;
    }

    // Quantity Minus
    if (target.closest('[data-action="minus"]')) {
      const item = target.closest('.cart-drawer-item');
      const input = item?.querySelector('.quantity-selector__input');
      if (item && input) {
        const key = item.dataset.lineItemKey;
        const newVal = Math.max(0, (parseInt(input.value) || 0) - 1);
        input.value = newVal;
        this.debouncedUpdate(key, newVal);
      }
      return;
    }

    // Direct input change
    if (target.matches('.quantity-selector__input') && event.type === 'change') {
      const item = target.closest('.cart-drawer-item');
      if (item) {
        const key = item.dataset.lineItemKey;
        const quantity = parseInt(target.value);
        if (key && !isNaN(quantity)) {
          this.debouncedUpdate(key, Math.max(0, quantity));
        }
      }
    }
  }

  /* --- Cart API calls --- */
  async changeItem(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await response.json();
      await this.refresh();
      this.updateCartCounts(cart.item_count);
    } catch (error) {
      console.error('Error changing item:', error);
      this.showToast('Bir hata oluştu', 'error');
    }
  }

  async addUpsellItem(variantId, btn) {
    if (btn) {
      btn.classList.add('loading');
      btn.disabled = true;
    }

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
      });
      await this.refresh();
      this.showToast('Ürün sepete eklendi!');
    } catch (error) {
      console.error('Error adding upsell:', error);
    } finally {
      if (btn) {
        btn.classList.remove('loading');
        btn.disabled = false;
      }
    }
  }

  async addBundle(variantIds, btn) {
    if (!variantIds) return;
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Ekleniyor...';
    }

    try {
      const ids = variantIds.split(',').map(id => id.trim());
      const items = ids.map(id => ({ id: parseInt(id), quantity: 1 }));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        if (btn) btn.textContent = 'Eklendi!';
        if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        } else {
          await this.refresh();
          this.open();
        }
        this.showToast('Paket sepete eklendi!');
      } else {
        const data = await response.json();
        throw new Error(data.description || 'Hata oluştu');
      }
    } catch (error) {
      console.error('Bundle add error:', error);
      if (btn) btn.textContent = 'Hata!';
      this.showToast(error.message, 'error');
    } finally {
      setTimeout(() => {
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Ekle';
        }
      }, 2000);
    }
  }

  /* --- Refresh drawer HTML + badge counts --- */
  async refresh() {
    try {
      const cartData = await (await fetch('/cart.js')).json();

      // Auto-remove excess promo gifts if real items dropped below threshold
      const cleaned = await this.enforcePromoGiftLimits(cartData);
      const finalCart = cleaned || cartData;

      const sectionHtml = await (await fetch('/cart?section_id=cart-drawer')).text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');

      const newDrawer = doc.querySelector('.cart-drawer__inner');
      const currentDrawer = this.querySelector('.cart-drawer__inner');

      if (newDrawer && currentDrawer) {
        currentDrawer.innerHTML = newDrawer.innerHTML;
      }

      // Toggle is-empty class
      if (finalCart.item_count === 0) {
        this.classList.add('is-empty');
      } else {
        this.classList.remove('is-empty');
      }

      this.updateCartCounts(finalCart.item_count);
    } catch (error) {
      console.error('Cart drawer refresh error:', error);
    }
  }

  /* --- Enforce promo gift limits: N items → N-1 gifts (min 3 items) --- */
  async enforcePromoGiftLimits(cartData) {
    let realCount = 0;
    const giftItems = [];

    for (const item of cartData.items) {
      const isGift = item.properties && item.properties._promo_gift === 'true';
      if (isGift) {
        giftItems.push(item);
      } else {
        realCount += item.quantity;
      }
    }

    const allowedGifts = realCount >= 3 ? realCount - 1 : 0;
    const currentGiftCount = giftItems.reduce((sum, item) => sum + item.quantity, 0);

    if (currentGiftCount <= allowedGifts) return null;

    // Need to remove excess gifts (remove from last added first)
    let toRemove = currentGiftCount - allowedGifts;
    const updates = {};

    for (let i = giftItems.length - 1; i >= 0 && toRemove > 0; i--) {
      const item = giftItems[i];
      if (item.quantity <= toRemove) {
        updates[item.key] = 0;
        toRemove -= item.quantity;
      } else {
        updates[item.key] = item.quantity - toRemove;
        toRemove = 0;
      }
    }

    try {
      const resp = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      return await resp.json();
    } catch (e) {
      console.error('Error removing excess gifts:', e);
      return null;
    }
  }

  updateCartCounts(count) {
    document.querySelectorAll('[data-cart-count], .mobile-bottom-nav__badge, .cart-count-bubble, .header__cart-badge').forEach(el => {
      el.textContent = count;
      if (count === 0) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
    // Also update cart drawer header count
    const drawerCount = this.querySelector('.cart-drawer__count');
    if (drawerCount) drawerCount.textContent = `(${count})`;
  }

  /* --- Toast --- */
  showToast(message, type = 'success') {
    let toast = document.querySelector('.cart-drawer-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'cart-drawer-toast';
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.dataset.type = type;

    // Force reflow to restart animation
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');

    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 3000);
  }

  debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

/* --- Promo Gift (her 3 üründe 1 hediye) --- */
(function() {
  function initPromoGift() {
    // Promo gift add buttons
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-promo-add]');
      if (!btn || btn.disabled) return;

      var variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.classList.add('is-loading');
      btn.textContent = 'Ekleniyor...';
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1,
            properties: {
              '_promo_gift': 'true',
              'Hediye': 'Evet'
            }
          }]
        })
      })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.status) {
          btn.textContent = 'Hata!';
          btn.classList.remove('is-loading');
          setTimeout(function() {
            btn.textContent = 'Hediye Olarak Seç';
            btn.disabled = false;
          }, 2000);
          return;
        }

        btn.textContent = 'Seçildi!';
        btn.classList.remove('is-loading');

        // Refresh cart drawer
        var drawer = document.querySelector('cart-drawer');
        if (drawer && drawer.refresh) {
          drawer.refresh();
          drawer.showToast('Hediye ürün sepete eklendi!');
        }

        // If on cart page, reload
        if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        }
      })
      .catch(function() {
        btn.textContent = 'Hata!';
        btn.classList.remove('is-loading');
        setTimeout(function() {
          btn.textContent = 'Hediye Olarak Seç';
          btn.disabled = false;
        }, 2000);
      });
    });

    // Promo gift slider arrows
    document.addEventListener('click', function(e) {
      var prevBtn = e.target.closest('[data-promo-prev]');
      var nextBtn = e.target.closest('[data-promo-next]');
      if (!prevBtn && !nextBtn) return;

      var wrap = (prevBtn || nextBtn).closest('.promo-gift__slider-wrap');
      var track = wrap ? wrap.querySelector('[data-promo-track]') : null;
      if (!track) return;

      var card = track.querySelector('.promo-gift__card');
      if (!card) return;

      var gap = parseFloat(getComputedStyle(track).gap) || 12;
      var scrollAmount = card.offsetWidth + gap;

      if (prevBtn) {
        track.scrollBy({ left: -scrollAmount * 2, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: scrollAmount * 2, behavior: 'smooth' });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPromoGift);
  } else {
    initPromoGift();
  }
})();

/* --- Predictive Search --- */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('.predictive-search__results');
    this.abortController = null;

    if (this.input) {
      this.input.addEventListener('input', this.debounce(this.onInput.bind(this), 300));
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.close();
      }
    });
  }

  async onInput() {
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.close();
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    try {
      const response = await fetch(
        `${window.routes.predictiveSearch}?q=${encodeURIComponent(query)}&resources[type]=product,page,article&resources[limit]=4&section_id=predictive-search`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newResults = doc.querySelector('.predictive-search__results');

      if (newResults && this.results) {
        this.results.innerHTML = newResults.innerHTML;
        this.open();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Predictive search error:', error);
      }
    }
  }

  onFocus() {
    if (this.input.value.trim().length >= 2 && this.results?.innerHTML.trim()) {
      this.open();
    }
  }

  open() {
    this.classList.add('is-open');
    this.results?.setAttribute('aria-hidden', 'false');
  }

  close() {
    this.classList.remove('is-open');
    this.results?.setAttribute('aria-hidden', 'true');
  }

  debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

/* --- Announcement Bar Marquee --- */
class MarqueeText extends HTMLElement {
  constructor() {
    super();
    this.speed = parseInt(this.dataset.speed) || 30;
    this.init();
  }

  init() {
    const track = this.querySelector('.marquee__track');
    if (!track) return;

    const content = track.innerHTML;
    track.innerHTML = content + content;

    const width = track.scrollWidth / 2;
    track.style.setProperty('--marquee-width', `${width}px`);
    track.style.setProperty('--marquee-duration', `${width / this.speed}s`);
  }
}
if (!customElements.get('marquee-text')) customElements.define('marquee-text', MarqueeText);

/* --- Slideshow --- */
class SlideshowComponent extends HTMLElement {
  constructor() {
    super();
    this.slides = this.querySelectorAll('.slideshow__slide');
    this.dots = this.querySelectorAll('.slideshow__dot');
    this.prevButton = this.querySelector('[data-slideshow-prev]');
    this.nextButton = this.querySelector('[data-slideshow-next]');
    this.currentIndex = 0;
    this.autoplay = this.dataset.autoplay === 'true';
    this.interval = parseInt(this.dataset.interval) || 5000;
    this.autoplayTimer = null;

    if (this.slides.length <= 1) return;

    this.prevButton?.addEventListener('click', () => this.prev());
    this.nextButton?.addEventListener('click', () => this.next());

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });

    if (this.autoplay) {
      this.startAutoplay();
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
    }

    this.goTo(0);
  }

  goTo(index) {
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
      slide.setAttribute('aria-hidden', i !== index);
    });

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    this.currentIndex = index;
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goTo(nextIndex);
  }

  prev() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goTo(prevIndex);
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => this.next(), this.interval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}
if (!customElements.get('slideshow-component')) customElements.define('slideshow-component', SlideshowComponent);

/* --- Collapsible Content --- */
class CollapsibleContent extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('.collapsible__trigger');
    this.content = this.querySelector('.collapsible__content');

    if (this.trigger) {
      this.trigger.addEventListener('click', this.toggle.bind(this));
    }
  }

  toggle() {
    const isOpen = this.classList.contains('is-open');
    this.classList.toggle('is-open');
    this.trigger.setAttribute('aria-expanded', !isOpen);
    this.content.setAttribute('aria-hidden', isOpen);
  }
}
if (!customElements.get('collapsible-content')) customElements.define('collapsible-content', CollapsibleContent);

/* --- Pickup Availability --- */
class PickupAvailability extends HTMLElement {
  constructor() {
    super();
    if (this.dataset.variantId) {
      this.fetchAvailability(this.dataset.variantId);
    }
  }

  async fetchAvailability(variantId) {
    try {
      const response = await fetch(
        `${window.shopUrl}/variants/${variantId}/?section_id=pickup-availability`
      );
      if (!response.ok) {
        this.innerHTML = '';
        return;
      }
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const content = doc.querySelector('.pickup-availability');
      this.innerHTML = content ? content.innerHTML : '';
    } catch (error) {
      this.innerHTML = '';
    }
  }
}
if (!customElements.get('pickup-availability')) customElements.define('pickup-availability', PickupAvailability);

/* --- Deferred Media (video, 3D) --- */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    this.poster = this.querySelector('.deferred-media__poster');
    if (this.poster) {
      this.poster.addEventListener('click', this.loadContent.bind(this));
    }
  }

  loadContent() {
    const template = this.querySelector('template');
    if (!template) return;
    this.appendChild(template.content.firstElementChild.cloneNode(true));
    this.classList.add('is-loaded');
  }
}
if (!customElements.get('deferred-media')) customElements.define('deferred-media', DeferredMedia);

/* --- Countdown Timer --- */
class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.endDate = new Date(this.dataset.endDate).getTime();
    this.daysEl = this.querySelector('[data-days]');
    this.hoursEl = this.querySelector('[data-hours]');
    this.minutesEl = this.querySelector('[data-minutes]');
    this.secondsEl = this.querySelector('[data-seconds]');

    if (this.endDate) {
      this.update();
      this.timer = setInterval(() => this.update(), 1000);
    }
  }

  update() {
    const now = new Date().getTime();
    const distance = this.endDate - now;

    if (distance < 0) {
      clearInterval(this.timer);
      this.classList.add('expired');
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    if (this.daysEl) this.daysEl.textContent = String(days).padStart(2, '0');
    if (this.hoursEl) this.hoursEl.textContent = String(hours).padStart(2, '0');
    if (this.minutesEl) this.minutesEl.textContent = String(minutes).padStart(2, '0');
    if (this.secondsEl) this.secondsEl.textContent = String(seconds).padStart(2, '0');
  }

  disconnectedCallback() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }
}
if (!customElements.get('countdown-timer')) customElements.define('countdown-timer', CountdownTimer);

/* --- Faceted Filtering --- */
class FacetedFiltering extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.resultsContainer = document.querySelector('#collection-products');

    if (this.form) {
      this.form.addEventListener('change', this.debounce(this.onSubmit.bind(this), 500));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    const formData = new FormData(this.form);
    const searchParams = new URLSearchParams(formData).toString();
    const url = `${window.location.pathname}?${searchParams}`;

    window.history.pushState({}, '', url);

    try {
      const response = await fetch(`${url}&section_id=${this.dataset.sectionId}`);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newResults = doc.querySelector('#collection-products');
      if (newResults && this.resultsContainer) {
        this.resultsContainer.innerHTML = newResults.innerHTML;
      }

      const newFilters = doc.querySelector('faceted-filtering');
      if (newFilters) {
        this.innerHTML = newFilters.innerHTML;
      }
    } catch (error) {
      console.error('Filtering error:', error);
    }
  }

  debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }
}
if (!customElements.get('faceted-filtering')) customElements.define('faceted-filtering', FacetedFiltering);

/* --- Cart Remove Button (only outside cart page) --- */
document.addEventListener('DOMContentLoaded', () => {
  document.addEventListener('click', (event) => {
    if (event.target.closest('.cart-item__remove')) {
      // Skip if inside cart page (it has its own handler)
      if (event.target.closest('.cart-page')) return;
      const button = event.target.closest('.cart-item__remove');
      const form = button.closest('form');
      if (!form) return;
      const lineIndex = button.dataset.line;
      const input = form.querySelector(`[data-line="${lineIndex}"]`);
      if (input) {
        input.value = 0;
        form.submit();
      }
    }
  });
});
